import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { brandHeroGradient } from '../theme/styleHelpers';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { authService } from '../services';

/**
 * Forgot Password Page
 *
 * Allows users to request a password reset link via email
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const { mode } = useTheme();
  const muiTheme = useMuiTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Basic email validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.forgotPassword(email);
      setSuccess(true);
      console.log('Password reset email sent:', response);
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
      {/* Brand Logo/Header - Outside the dialog box */}
      <Box
        component={Link}
        to="/"
        sx={{
          mt: { xs: 4, sm: 6 },
          mb: 4,
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <Typography
          variant="h3"
          component="div"
          sx={{
            fontWeight: 800,
            background: brandHeroGradient(muiTheme),
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}
        >
          Tier One Content
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.75rem' }}
        >
          Premium Content Sharing Platform
        </Typography>
      </Box>

      {/* Forgot Password Form */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        {!success ? (
          <>
            <Typography component="h1" variant="h5" gutterBottom>
              Reset Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !email}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  to="/login"
                  style={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: mode === 'dark' ? '#00d4aa' : '#00a884',
                  }}
                >
                  <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Back to Login
                </Link>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography component="h1" variant="h5" gutterBottom>
                Check Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We've sent a password reset link to <strong>{email}</strong>.
                <br />
                <br />
                Click the link in the email to reset your password. The link will expire in 1 hour.
              </Typography>

              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Didn't receive the email?</strong>
                </Typography>
                <Typography variant="caption">
                  • Check your spam or junk folder
                  <br />
                  • Make sure you entered the correct email address
                  <br />
                  • Wait a few minutes and check again
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  fullWidth
                >
                  Try Another Email
                </Button>

                <Link
                  to="/login"
                  style={{ textDecoration: 'none', width: '100%' }}
                >
                  <Button
                    variant="text"
                    fullWidth
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to Login
                  </Button>
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Additional Help */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help?{' '}
          <Link
            to="/help"
            style={{
              color: mode === 'dark' ? '#00d4aa' : '#00a884',
              textDecoration: 'none',
            }}
          >
            Contact Support
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
